import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import type { LoadingComponentProps } from '@atlassian/native-embeds-core/types';

import messages from './messages';

const BAR_HEIGHTS = ['15%', '40%', '60%', '35%', '80%', '50%', '25%'] as const;

const styles = cssMap({
	container: {
		position: 'absolute',
		inset: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
		paddingBlock: token('space.300'),
		paddingInline: token('space.300'),
		backgroundColor: token('elevation.surface'),
		boxSizing: 'border-box',
	},
	plot: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-end',
		gap: token('space.100'),
		flexGrow: 1,
		minHeight: 0,
	},
});

// Match avp/services/viz/src/components/ChartSkeleton.tsx so the iframe handoff does not jump.
/**
 * Mirrors the Bifrost loading layout while respecting the host's animation preference. Exposed as an
 * indeterminate progress bar, like core's loading overlay; its skeleton children are presentational.
 */
export function ChartLoading({
	animationDisabled = false,
	testId,
}: LoadingComponentProps): JSX.Element {
	const intl = useIntl();
	return (
		<Box
			xcss={styles.container}
			testId={testId}
			role="progressbar"
			aria-label={intl.formatMessage(messages.loading)}
		>
			<Stack space="space.100">
				<Skeleton width="55%" height="16px" isShimmering={!animationDisabled} />
				<Skeleton width="35%" height="12px" isShimmering={!animationDisabled} />
			</Stack>
			<Box xcss={styles.plot}>
				{BAR_HEIGHTS.map((height) => (
					<Skeleton key={height} width="100%" height={height} isShimmering={!animationDisabled} />
				))}
			</Box>
		</Box>
	);
}
